import React from "react";
import { sortByAccessor } from "../../../utils/sortRows";
import { savedColumns } from "../../../hooks/useTableColumns";
import { TwoLine, Pill, Meter, marginTone, utilColor, hoursColor, UTIL_TARGET } from "./cells";
import styles from "./Reports.module.css";

export const REPORT_TABS = [
  { value: "overview", label: "Overview" },
  { value: "financial", label: "Financial" },
  { value: "sales", label: "Sales" },
  { value: "projects", label: "Projects" },
  { value: "team", label: "Team & timesheets" },
  { value: "leave", label: "Leave" },
  { value: "tasks", label: "Tasks" },
];

export const PERIODS = [
  { value: "last_month", label: "Last month" },
  { value: "quarter", label: "Quarter" },
  { value: "ytd", label: "Year to date" },
  { value: "12m", label: "12 months" },
];

export const PEOPLE = [
  { value: "all", label: "All People" },
  { value: "permanent", label: "Permanent staff" },
  { value: "contractor", label: "Contractors" },
];

const ratio = (a, b) => (b ? a / b : null);
const sum = (rows, key) => rows.reduce((acc, r) => acc + (Number(r[key]) || 0), 0);
const plural = (n, one, many = `${one}s`) => `${n} ${Number(n) === 1 ? one : many}`;
const shortDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });

// One table column. `format` decides the plain cell and the Excel value; `render` draws a richer cell on screen.
// Plain figures line up on the right; pills, bars and text stay left.
const col = (key, label, format = "text", extra = {}) => ({
  key, label, format, visible: true, align: format !== "text" && !(format === "pct" && extra.render) ? "right" : undefined, ...extra,
});
const hidden = { visible: false };

export const TABLES = {
  pnl: {
    title: "Profit and loss by month",
    storageKey: "reportPnlColumns",
    rowId: (r) => r.month,
    rows: (r) => r.financial.monthly,
    footer: (rows) => {
      const revenue = sum(rows, "revenue");
      const labour = sum(rows, "labour");
      return { label: "Total", revenue, labour, gross: revenue - labour, margin: ratio(revenue - labour, revenue) };
    },
    columns: [
      col("label", "Month", "text", { sort: (r) => r.month }),
      col("revenue", "Revenue", "money", { render: (r, f) => <strong>{f.money(r.revenue)}</strong> }),
      col("labour", "Labour cost", "money"),
      col("gross", "Gross profit", "money", { render: (r, f) => <strong className={styles.good}>{f.money(r.gross)}</strong> }),
      col("margin", "Margin", "pct"),
    ],
  },
  profitability: {
    title: "Project profitability",
    sub: "Recognised = budget × milestones complete · red below 20% margin, amber below 35%",
    storageKey: "reportProfitabilityColumns",
    rowId: (r) => r.project_id,
    rows: (r) => r.financial.projects,
    footer: (rows) => ({ project_name: plural(rows.length, "project"), budget: sum(rows, "budget"), recognised: sum(rows, "recognised"), labour: sum(rows, "labour") }),
    columns: [
      col("project_name", "Project", "text", { render: (r) => <TwoLine main={r.project_name} sub={r.client_name} /> }),
      col("budget", "Budget", "money", { render: (r, f) => f.compact(r.budget) }),
      col("recognised", "Recognised", "money", { render: (r, f) => <TwoLine main={f.compact(r.recognised)} sub={`${f.pct(r.progress)} done`} /> }),
      col("labour", "Labour + hours", "money", { render: (r, f) => <TwoLine main={f.compact(r.labour)} sub={f.hours(r.used_hours)} /> }),
      col("margin", "Margin", "pct", { render: (r, f) => <Pill tone={marginTone(r.margin)}>{f.pct(r.margin)}</Pill> }),
      col("hours_used", "Hours used", "pct", {
        render: (r) => <Meter text={`${Math.round(r.used_hours)}/${Math.round(r.budget_hours)}h`} value={r.hours_used || 0} color={hoursColor(r.hours_used)} className={r.hours_used > 1 ? styles.bad : ""} />,
      }),
      col("client_name", "Client", "text", hidden),
      col("status_name", "Status", "text", hidden),
    ],
  },
  leaveLiability: {
    title: "Leave liability",
    sub: "Permanent staff only · unused annual leave × daily cost (cost rate × 8h)",
    storageKey: "reportLeaveLiabilityColumns",
    rowId: (r) => r.employee_id,
    rows: (r) => r.leave.people.map((p) => ({ ...p, daily_cost: p.rate * 8 })),
    footer: (rows) => ({ name: "Total", remaining: sum(rows, "remaining"), liability: sum(rows, "liability") }),
    columns: [
      col("name", "Employee"),
      col("remaining", "Days left", "days"),
      col("daily_cost", "Daily cost", "money"),
      col("liability", "Liability", "money", { render: (r, f) => <strong className={styles.brand}>{f.money(r.liability)}</strong> }),
    ],
  },
  owners: {
    title: "By owner",
    sub: "Open pipeline, and leads won and lost in the period",
    storageKey: "reportOwnerColumns",
    rowId: (r) => r.owner,
    rows: (r) => r.sales.owners,
    columns: [
      col("owner", "Owner"),
      col("open", "Open", "int"),
      col("pipeline", "Pipeline", "money", { render: (r, f) => f.compact(r.pipeline) }),
      col("won", "Won", "int"),
      col("lost", "Lost", "int"),
      col("win_rate", "Win rate", "pct"),
    ],
  },
  projectStatus: {
    title: "Project status",
    sub: "Progress, deadlines and milestones",
    storageKey: "reportProjectStatusColumns",
    rowId: (r) => r.project_id,
    rows: (r) => r.projects.rows,
    columns: [
      col("project_name", "Project", "text", { render: (r) => <TwoLine main={r.project_name} sub={r.client_name} /> }),
      col("status_name", "Status", "text", { render: (r) => (r.status_name ? <Pill color={r.status_color || "#64748b"}>{r.status_name}</Pill> : "—") }),
      col("progress", "Progress", "pct", { render: (r, f) => <Meter text={f.pct(r.progress)} value={r.progress} color={r.completed ? "#16a34a" : "#185FA5"} /> }),
      col("end_date", "End date", "text", {
        render: (r) => (!r.end_date ? "—" : r.days_late
          ? <TwoLine className={styles.bad} main={shortDate(r.end_date)} sub={`${plural(r.days_late, "day")} late`} />
          : shortDate(r.end_date)),
      }),
      col("milestones_done", "Milestones", "int", { render: (r) => `${r.milestones_done}/${r.milestones_total}` }),
      col("client_name", "Client", "text", hidden),
      col("milestones_overdue", "Overdue milestones", "int", hidden),
    ],
  },
  teamCompare: {
    title: "Permanent staff and contractors",
    sub: "Permanent cost = capacity × cost rate · contractors = hours logged × rate",
    storageKey: "reportTeamCompareColumns",
    rowId: (r) => r.type,
    rows: (r) => r.team.groups,
    columns: [
      col("type", "Type", "text", { render: (r) => <Pill tone={r.type === "Contractor" ? "amber" : "blue"}>{r.type === "Contractor" ? "Contractors" : "Permanent"}</Pill> }),
      col("people", "People", "int"),
      col("hours", "Logged", "hours"),
      col("billable", "Billable", "hours"),
      col("utilisation", "Utilisation", "pct", { render: (r, f) => <Meter text={f.pct(r.utilisation)} value={r.utilisation || 0} color={utilColor(r.utilisation)} /> }),
      col("cost", "Cost", "money", { render: (r, f) => <strong>{f.compact(r.cost)}</strong> }),
      col("cost_per_billable_hour", "Cost per billable hour", "money"),
      col("capacity", "Capacity", "hours", hidden),
    ],
  },
  timesheets: {
    title: "Timesheet summary",
    storageKey: "reportTimesheetColumns",
    rowId: (r) => r.employee_id,
    rows: (r) => r.team.people,
    footer: (rows) => {
      const perm = rows.filter((p) => p.type === "Permanent").length;
      return { name: "Total", type: `${perm} + ${rows.length - perm}`, capacity: sum(rows, "capacity"), hours: sum(rows, "hours"), billable: sum(rows, "billable"), utilisation: ratio(sum(rows, "billable"), sum(rows, "capacity")) };
    },
    columns: [
      col("name", "Name", "text", { render: (r) => <TwoLine main={r.name} sub={r.type === "Contractor" ? "Paid per hour logged" : null} /> }),
      col("type", "Type", "text", { render: (r) => <Pill tone={r.type === "Contractor" ? "amber" : "blue"}>{r.type}</Pill> }),
      col("capacity", "Capacity", "hours"),
      col("hours", "Logged", "hours", { render: (r, f) => <span className={r.over_capacity ? styles.bad : ""}>{f.hours(r.hours)}</span> }),
      col("billable", "Billable", "hours"),
      col("utilisation", "Utilisation", "pct", { render: (r, f) => <Meter text={f.pct(r.utilisation)} value={r.utilisation || 0} color={utilColor(r.utilisation)} /> }),
      col("cost", "Cost", "money", hidden),
      col("cost_per_billable_hour", "Cost per billable hour", "money", hidden),
    ],
  },
  leaveBalances: {
    title: "Annual leave balances",
    sub: (r) => `Permanent staff only · contractors don't accrue leave (${r.leave.contractors_excluded} excluded)`,
    storageKey: "reportLeaveBalanceColumns",
    rowId: (r) => r.employee_id,
    rows: (r) => r.leave.people,
    columns: [
      col("name", "Employee"),
      col("used", "Used", "days"),
      col("remaining", "Left", "days", { render: (r, f) => <strong className={styles.good}>{f.days(r.remaining)}</strong> }),
      col("liability", "Liability", "money"),
      col("entitled", "Entitled", "days", hidden),
      col("carry_over", "Carried over", "days", hidden),
    ],
  },
  assignees: {
    title: "By assignee",
    storageKey: "reportAssigneeColumns",
    rowId: (r) => r.employee_id,
    rows: (r) => r.tasks.by_assignee,
    columns: [
      col("name", "Assignee"),
      col("open", "Open", "int"),
      col("done", "Done", "int"),
      col("overdue", "Overdue", "int", { render: (r) => <span className={r.overdue ? styles.bad : ""}>{r.overdue}</span> }),
      col("on_time", "On time", "pct"),
    ],
  },
};

// What each tab shows below its cards, in order. A pair sits side by side.
export const TAB_LAYOUT = {
  overview: [["chart:pnl", "chart:topClients"], ["chart:utilisation", "chart:stages"]],
  financial: ["chart:pnl", "pnl", "profitability", ["chart:clients", "chart:forecast"], "leaveLiability"],
  sales: [["chart:stages", "chart:lostReasons"], "chart:wonLost", "owners"],
  projects: [["chart:projectStatus", "chart:hoursUsed"], "projectStatus"],
  team: ["teamCompare", "chart:utilisation", "timesheets"],
  leave: [["chart:leaveTypes", "chart:leaveMonths"], "leaveBalances"],
  tasks: ["chart:recurring", "assignees"],
};

export const tablesOf = (tab) => TAB_LAYOUT[tab].flat().filter((item) => !item.startsWith("chart:"));

export const change = (cur, prev) => (cur == null || !prev ? null : (cur - prev) / Math.abs(prev));
const arrow = (up) => (up ? "▲" : "▼");
const pctDelta = (cur, prev) => {
  const d = change(cur, prev);
  return d == null ? null : { up: d >= 0, text: `${arrow(d >= 0)} ${Math.round(Math.abs(d) * 100)}%` };
};
const ptsDelta = (cur, prev) => (cur == null || prev == null ? null : { up: cur >= prev, text: `${arrow(cur >= prev)} ${Math.abs(Math.round((cur - prev) * 1000) / 10)} pts` });

// Summary cards per tab. `value` is the raw number exported to Excel; `display` is what the card shows.
export const tabCards = (tab, r, f) => {
  const { financial: fin, sales, projects, team, leave, tasks } = r;
  const vs = (delta) => (delta ? `vs ${r.period.compare_label}` : `No data for ${r.period.compare_label}`);
  const card = (label, value, display, sub, extra = {}) => ({ label, value, display, sub, ...extra });
  const margin = ratio(fin.gross, fin.revenue);
  const rate = ratio(fin.revenue, fin.billable_hours);
  const [permanent, contractor] = team.groups;
  const revenueDelta = pctDelta(fin.revenue, fin.prev_revenue);
  const cards = {
    revenue: card("Revenue", fin.revenue, f.compact(fin.revenue), vs(revenueDelta), { tone: "primary", delta: revenueDelta }),
    liability: card("Leave liability", leave.liability, f.compact(leave.liability), "Permanent staff, unused annual leave", { tone: "success" }),
    weighted: card("Weighted pipeline", sales.weighted_pipeline, f.compact(sales.weighted_pipeline), "By stage probability"),
    utilisation: card("Billable utilisation", team.utilisation, f.pct(team.utilisation), `Target ${f.pct(UTIL_TARGET)}`, { delta: ptsDelta(team.utilisation, team.prev_utilisation) }),
    booked: card("Booked revenue", fin.booked, f.compact(fin.booked), "Left to deliver on projects"),
  };
  switch (tab) {
    case "overview":
      return [
        cards.revenue,
        card("Gross margin", margin, f.pct(margin), `${f.compact(fin.gross)} gross profit`, { delta: ptsDelta(margin, ratio(fin.prev_gross, fin.prev_revenue)) }),
        { ...cards.weighted, sub: plural(sales.open_leads, "open lead") },
        cards.utilisation,
        card("Overdue milestones", projects.milestones_overdue, String(projects.milestones_overdue), `${plural(projects.projects_overdue, "project")} affected`),
        cards.liability,
      ];
    case "financial":
      return [
        cards.revenue,
        card("Labour cost", fin.labour, f.compact(fin.labour),
          fin.labour ? `Permanent ${f.pct(ratio(fin.labour_permanent, fin.labour))} · contractors ${f.pct(ratio(fin.labour_contractor, fin.labour))}` : "No approved hours"),
        card("Gross profit", fin.gross, f.compact(fin.gross), margin == null ? "No revenue yet" : `${f.pct(margin)} margin`, { delta: ptsDelta(margin, ratio(fin.prev_gross, fin.prev_revenue)) }),
        card("Effective rate", rate, rate == null ? "—" : `${f.money(rate)}/h`, "Revenue per billable hour", { delta: pctDelta(rate, ratio(fin.prev_revenue, fin.prev_billable_hours)) }),
        cards.booked,
        cards.liability,
      ];
    case "sales":
      return [
        card("Open pipeline", sales.pipeline, f.compact(sales.pipeline), plural(sales.open_leads, "lead"), { tone: "primary" }),
        cards.weighted,
        card("Win rate", sales.win_rate, f.pct(sales.win_rate), `${sales.won} won · ${sales.lost} lost`, { delta: ptsDelta(sales.win_rate, sales.prev_win_rate) }),
        card("Average deal", sales.avg_deal, sales.avg_deal == null ? "—" : f.compact(sales.avg_deal), "Won leads"),
        card("Days to close", sales.days_to_close, sales.days_to_close == null ? "—" : String(Math.round(sales.days_to_close)), "Median, lead created → won"),
      ];
    case "projects": {
      const onSchedule = ratio(projects.active - projects.late, projects.active);
      return [
        card("Active projects", projects.active, String(projects.active), `${projects.completed} completed`, { tone: "primary" }),
        card("On schedule", onSchedule, f.pct(onSchedule), `${projects.late} past end date`),
        card("Overdue milestones", projects.milestones_overdue, String(projects.milestones_overdue), `Across ${plural(projects.projects_overdue, "project")}`),
        card("Average progress", projects.avg_progress, f.pct(projects.avg_progress), "Milestones complete"),
        card("Over hours budget", projects.over_hours, String(projects.over_hours), "Used more hours than estimated", projects.over_hours ? { tone: "danger" } : {}),
      ];
    }
    case "team":
      return [
        { ...cards.utilisation, tone: "primary" },
        card("Hours logged", team.hours, f.hours(team.hours), `of ${f.hours(team.capacity)} capacity`),
        card("Billable hours", team.billable, f.hours(team.billable), `${f.pct(ratio(team.billable, team.hours))} of logged`),
        card("Over capacity", team.over_capacity, String(team.over_capacity), "Logged more than capacity"),
        card("Permanent cost", permanent.cost, f.compact(permanent.cost), `${plural(permanent.people, "person", "people")} · fixed, capacity × cost rate`),
        card("Contractor cost", contractor.cost, f.compact(contractor.cost), `${plural(contractor.people, "contractor")} · hours logged × rate`),
      ];
    case "leave":
      return [
        card("Days taken", leave.taken, f.num(leave.taken), `${r.period.label}, all types`, { tone: "primary", delta: pctDelta(leave.taken, leave.prev_taken) }),
        card("Pending requests", leave.pending.count, String(leave.pending.count), plural(f.num(leave.pending.days), "day")),
        card("Off this month", leave.off_this_month.people, plural(leave.off_this_month.people, "person", "people"), `${plural(f.num(leave.off_this_month.days), "day")} booked`),
        cards.liability,
      ];
    case "tasks":
      return [
        card("Open tasks", tasks.open, String(tasks.open), "Admin and accounting", { tone: "primary" }),
        card("Completed", tasks.done, String(tasks.done), r.period.label, { delta: pctDelta(tasks.done, tasks.prev_done) }),
        card("Overdue", tasks.overdue, String(tasks.overdue), "Past due date", tasks.overdue ? { tone: "danger" } : {}),
        card("On time", tasks.on_time, f.pct(tasks.on_time), "Completed by due date"),
      ];
    default:
      return [];
  }
};

// ---- Table state shared by the on-screen table and the Excel export ----

export const loadSort = (storageKey) => {
  try {
    return JSON.parse(localStorage.getItem(`${storageKey}:sort`)) || null;
  } catch {
    return null;
  }
};

export const saveSort = (storageKey, sort) => {
  try {
    localStorage.setItem(`${storageKey}:sort`, JSON.stringify(sort));
  } catch {
    // Storage unavailable — sort just doesn't persist.
  }
};

const columnAccessor = (column) => column.sort || ((row) => row[column.key]);

export const sortTableRows = (def, rows, sort) => {
  const column = def.columns.find((c) => c.key === sort?.key);
  return column ? sortByAccessor(rows, sort, columnAccessor(column)) : rows;
};

// Money, hours and days go to Excel as numbers so totals work; percentages as whole-number percents.
const exportValue = (column) => (row) => {
  const v = row[column.key];
  if (column.format === "pct") return v == null ? null : Math.round(v * 1000) / 10;
  if (column.format === "text") return v ?? null;
  return v == null ? null : Math.round(Number(v) * 100) / 100;
};

export const exportColumns = (columns) =>
  columns
    .filter((c) => c.visible)
    .map((c) => ({ label: c.format === "pct" ? `${c.label} (%)` : c.label, value: exportValue(c) }));

// The sheet for one table as it looks on screen: saved visible columns, saved sort, totals last.
export const tableSheet = (key, report, onlyIds = null) => {
  const def = TABLES[key];
  let rows = sortTableRows(def, def.rows(report), loadSort(def.storageKey));
  if (onlyIds) rows = rows.filter((row) => onlyIds.has(def.rowId(row)));
  else if (def.footer && rows.length) rows = [...rows, def.footer(rows)];
  return { name: def.title, columns: exportColumns(savedColumns(def.storageKey, def.columns)), rows };
};

export const summarySheet = (tabs, report, f) => ({
  name: "Summary",
  columns: [
    { label: "Report", value: (c) => c.tab },
    { label: "Figure", value: (c) => c.label },
    { label: "Value", value: (c) => (c.value == null ? null : Math.round(c.value * 100) / 100) },
    { label: "Note", value: (c) => [c.delta?.text, c.sub].filter(Boolean).join(" ") },
  ],
  rows: [
    { tab: "Period", label: report.period.label, value: null, sub: `Compared with ${report.period.compare_label}` },
    ...tabs.flatMap((tab) => tabCards(tab, report, f).map((c) => ({ ...c, tab: REPORT_TABS.find((t) => t.value === tab).label }))),
  ],
});
